import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import HeadActionButtons from './ContentsHeadButtons';
import {
  SurveyNameCont,
  SurveyName,
  SurveyOrganizationCont,
  CompanyIcon,
  OrganizationName,
} from './CreateSurveyHeader.styles';
import { CreateSurveyCardsNames } from '../../constans/state';
import { useSurveyTable } from '../../hooks';
import { useAuth } from '../../hooks/useAuth';
import EyeIcon from '@/assets/icons/eyeIconWhite.svg?react';
import SaveIcon from '@/assets/icons/saveIcon.svg?react';
import SendIcon from '@/assets/icons/send.svg?react';

interface ICreateSurveyHeader {
  surveyName: string;
  organizationName: string;
  scroll: boolean;
  loading: boolean;
  onSubmitContents: () => Promise<void>;
  onShareResults: () => Promise<void>;
  preview_link: string;
  isDisabled: boolean;
}

const CreateSurveyHeader: FC<ICreateSurveyHeader> = ({
  surveyName,
  organizationName,
  scroll,
  loading,
  onSubmitContents,
  onShareResults,
  preview_link,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const { getAppliedFilters } = useSurveyTable();
  const { card } = getAppliedFilters();
  const { isEBHRMember } = useAuth();

  const renderButtons = useMemo(() => {
    if (card) {
      switch (card) {
        case CreateSurveyCardsNames.shareResults:
          return (
            <HeadActionButtons
              scroll={scroll}
              dark={[
                {
                  id: 'results-share',
                  callToAction: async () => await onShareResults(),
                  name: t('viewSurvey.topBar.share'),
                  icon: <SendIcon />,
                  loading,
                  disabled: isDisabled,
                },
              ]}
            />
          );
        case CreateSurveyCardsNames.contents:
        case CreateSurveyCardsNames.schedule:
          return (
            <HeadActionButtons
              scroll={scroll}
              dark={[
                ...(CreateSurveyCardsNames.contents === card
                  ? [
                    {
                      id: 'contents-preview',
                      callToAction: () => {
                        window.open(preview_link, '_blank');
                      },
                      name: t('viewSurvey.topBar.previewTheSurvey'),
                      icon: <EyeIcon />,
                      loading,
                    },
                  ]
                  : []),
                {
                  id: 'contents-save',
                  callToAction: async () => await onSubmitContents(),
                  name: t('viewSurvey.topBar.save'),
                  icon: <SaveIcon />,
                  loading,
                },
              ]}
            />
          );
      }
    }
    return;
  }, [card, scroll, onSubmitContents, onShareResults, t]);

  return (
    <>
      <SurveyNameCont id="SurveyNameCont">
        <SurveyName id="SurveyName" data-testid="surveyName" scroll={scroll}>
          {surveyName}
        </SurveyName>
        {renderButtons}
      </SurveyNameCont>
      {isEBHRMember ? (
        <SurveyOrganizationCont id="SurveyOrganizationCont" scroll={scroll}>
          <CompanyIcon id="CompanyIcon" /> {t('viewSurvey.topBar.organisation')}:&nbsp;
          <OrganizationName id="OrganizationName" data-testid="surveyOrganizationName">
            {organizationName}
          </OrganizationName>
        </SurveyOrganizationCont>
      ) : null}
    </>
  );
};

export default CreateSurveyHeader;
